/**
 * RepPacer — full-screen overlay that paces a single set using the user's
 * tempo (eccentric / pause-bottom / concentric / pause-top, in seconds).
 *
 * Lifecycle:
 *   1. Mount with target reps, tempo, cue mode, optional auto-start.
 *   2. Tick every 250ms — derives current phase from elapsed time.
 *   3. Voice/beep cue fires at each phase boundary; soft tick each second.
 *   4. When all reps complete (or user taps Stop), call `onComplete(reps_done)`.
 *
 * Designed to play over background music: WebAudio oscillators + brief TTS.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Play, Stop, ArrowsClockwise, XCircle } from '@phosphor-icons/react';
import { cue as fireCue, unlockAudio, getAudioPrefs } from '@/lib/audioCue';
import { CUES } from '@/constants/testIds';

const PHASES = [
    { id: 'ecc', label: 'Down', voice: 'Down' },
    { id: 'bot', label: 'Hold', voice: 'Hold' },
    { id: 'con', label: 'Up', voice: 'Up' },
    { id: 'top', label: 'Hold', voice: 'Hold' },
];

function tempoArray(t) {
    return [
        Math.max(0, t.tempo_eccentric ?? 3),
        Math.max(0, t.tempo_pause_bottom ?? 1),
        Math.max(0, t.tempo_concentric ?? 1),
        Math.max(0, t.tempo_pause_top ?? 0),
    ];
}

// Given seconds elapsed in current rep + the 4-phase tempo, return phase index
// and seconds-into-phase. If the tempo has a 0-second phase we skip past it.
function phaseAt(elapsed, durations) {
    let cursor = 0;
    for (let i = 0; i < 4; i++) {
        const d = durations[i];
        if (d <= 0) continue;
        if (elapsed < cursor + d) {
            return { idx: i, intoPhase: elapsed - cursor, dur: d };
        }
        cursor += d;
    }
    return { idx: -1, intoPhase: 0, dur: 0 };
}

export function RepPacer({
    open,
    targetReps,
    tempo,
    onComplete,
    onCancel,
    autoStart = false,
}) {
    const [startedAt, setStartedAt] = useState(null);
    const [now, setNow] = useState(Date.now());
    const lastBoundaryRef = useRef({ rep: -1, phaseIdx: -1, second: -1 });
    const completedRef = useRef(false);

    const durations = useMemo(() => tempoArray(tempo || {}), [tempo]);
    const repSeconds = durations.reduce((a, b) => a + b, 0) || 1;
    const totalSeconds = repSeconds * Math.max(1, targetReps || 1);

    const startNow = useCallback(() => {
        unlockAudio();
        completedRef.current = false;
        lastBoundaryRef.current = { rep: -1, phaseIdx: -1, second: -1 };
        setStartedAt(Date.now());
    }, []);

    // Auto-start on first open (if asked).
    useEffect(() => {
        if (open && autoStart && !startedAt) {
            startNow();
        }
        if (!open) {
            setStartedAt(null);
            completedRef.current = false;
        }
    }, [open, autoStart, startedAt, startNow]);

    // Ticker — 250ms.
    useEffect(() => {
        if (!open || !startedAt) return undefined;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [open, startedAt]);

    const elapsed = startedAt ? (now - startedAt) / 1000 : 0;
    const repIdx = startedAt
        ? Math.min(Math.floor(elapsed / repSeconds), Math.max(0, (targetReps || 1) - 1))
        : 0;
    const intoRep = startedAt ? elapsed - repIdx * repSeconds : 0;
    const ph = startedAt ? phaseAt(intoRep, durations) : { idx: -1, intoPhase: 0, dur: 0 };
    const remainingInPhase = Math.max(0, Math.ceil(ph.dur - ph.intoPhase));
    const repNumber = startedAt ? Math.min(repIdx + 1, targetReps || 1) : 0;
    const finished = startedAt && elapsed >= totalSeconds;

    // Fire cues at phase boundaries + each second tick.
    useEffect(() => {
        if (!open || !startedAt) return;
        const mode = getAudioPrefs().mode;
        const last = lastBoundaryRef.current;

        if (last.rep !== repIdx || last.phaseIdx !== ph.idx) {
            // Coach-style rep count: speak the rep number the moment the
            // user has just finished the concentric (Up) phase — that's when
            // a real spotter counts the rep aloud.
            const leftConcentric =
                last.phaseIdx === 2 &&
                (last.rep !== repIdx || ph.idx !== 2);

            if (leftConcentric) {
                const repN = (last.rep || 0) + 1;
                if (mode === 'voice') {
                    try {
                        if (typeof window !== 'undefined' && window.speechSynthesis) {
                            window.speechSynthesis.cancel();
                            const u = new window.SpeechSynthesisUtterance(
                                `${repN}`
                            );
                            u.rate = 1.0;
                            u.volume = 1.0;
                            u.pitch = 1.1;
                            window.speechSynthesis.speak(u);
                        }
                    } catch {
                        /* ignore */
                    }
                } else if (mode === 'beeps') {
                    // Sharp confirmation tone on rep-counted.
                    fireCue('go', { mode: 'beeps' });
                }
            } else if (ph.idx >= 0) {
                // Standard phase-name cue (Down / Hold / Up / Hold).
                if (mode === 'voice') {
                    try {
                        if (typeof window !== 'undefined' && window.speechSynthesis) {
                            window.speechSynthesis.cancel();
                            const u = new window.SpeechSynthesisUtterance(
                                PHASES[ph.idx].voice
                            );
                            u.rate = 1.1;
                            u.volume = 0.95;
                            window.speechSynthesis.speak(u);
                        }
                    } catch {
                        /* ignore */
                    }
                } else if (mode === 'beeps') {
                    const cueId = [
                        'countdown_5',
                        'countdown_2',
                        'go',
                        'countdown_3',
                    ][ph.idx];
                    fireCue(cueId, { mode: 'beeps' });
                }
            }
            lastBoundaryRef.current = {
                rep: repIdx,
                phaseIdx: ph.idx,
                second: -1,
            };
        }

        // Soft per-second click (beep mode only — voice mode is too noisy).
        const secNow = Math.floor(ph.intoPhase);
        if (
            mode === 'beeps' &&
            secNow !== lastBoundaryRef.current.second &&
            secNow > 0
        ) {
            // Reuse a very quiet countdown_1 as the per-second tick.
            fireCue('countdown_1', { mode: 'beeps' });
            lastBoundaryRef.current.second = secNow;
        }
    }, [open, startedAt, repIdx, ph.idx, ph.intoPhase, repNumber]);

    // When finished, call onComplete exactly once. The downstream
    // `completeSet` fires its own 'set_complete' cue so we don't double-fire
    // here — but we do speak a final "Done" so the user knows the count is
    // over (rep-N is already spoken by the boundary effect above).
    useEffect(() => {
        if (finished && !completedRef.current) {
            completedRef.current = true;
            try {
                const mode = getAudioPrefs().mode;
                if (
                    mode === 'voice' &&
                    typeof window !== 'undefined' &&
                    window.speechSynthesis
                ) {
                    // Defer slightly so we don't cancel the just-spoken count.
                    setTimeout(() => {
                        try {
                            const u = new window.SpeechSynthesisUtterance('Done');
                            u.rate = 1.0;
                            u.volume = 0.95;
                            window.speechSynthesis.speak(u);
                        } catch {
                            /* ignore */
                        }
                    }, 350);
                }
            } catch {
                /* ignore */
            }
            onComplete?.(targetReps);
        }
    }, [finished, onComplete, targetReps]);

    if (!open) return null;

    const phaseLabel = ph.idx >= 0 ? PHASES[ph.idx].label : '—';
    const tempoStr = durations.join('-');

    return (
        <div
            data-testid={CUES.pacerOverlay}
            role="dialog"
            aria-modal="true"
            aria-label="Rep pacer"
            className="fixed inset-0 z-50 grid place-items-center bg-background/80 backdrop-blur-md"
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel?.();
            }}
        >
            <div className="relative mx-5 w-full max-w-md rounded-3xl border border-foreground/10 bg-card p-7 shadow-tactile">
                <button
                    type="button"
                    onClick={onCancel}
                    aria-label="Close pacer"
                    className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full hover:bg-foreground/10 transition-colors text-muted-foreground"
                >
                    <XCircle size={22} weight="bold" />
                </button>

                <p className="uppercase-mono text-muted-foreground">
                    Tempo {tempoStr}
                </p>
                <h2 className="font-display font-extrabold tracking-tighter text-3xl mt-1">
                    Pace this set
                </h2>

                <div className="mt-7 grid place-items-center">
                    <div
                        className={`relative grid place-items-center w-44 h-44 rounded-full border-4 transition-colors ${
                            startedAt
                                ? 'border-[#00FF9D]/40 bg-[#00FF9D]/10'
                                : 'border-foreground/15 bg-foreground/5'
                        }`}
                    >
                        {startedAt ? (
                            <>
                                <p
                                    data-testid={CUES.pacerPhase}
                                    className="font-display font-extrabold tracking-tighter text-4xl"
                                >
                                    {phaseLabel.toUpperCase()}
                                </p>
                                <p className="font-mono text-2xl font-bold tabular-nums mt-1">
                                    {remainingInPhase}
                                    <span className="text-muted-foreground text-sm ml-1">
                                        s
                                    </span>
                                </p>
                            </>
                        ) : (
                            <p className="uppercase-mono text-muted-foreground">
                                Ready
                            </p>
                        )}
                    </div>
                    <p
                        data-testid={CUES.pacerRepCount}
                        className="mt-5 font-mono text-lg font-bold tabular-nums"
                    >
                        Rep {startedAt ? repNumber : 0}{' '}
                        <span className="text-muted-foreground">
                            / {targetReps || 0}
                        </span>
                    </p>
                </div>

                <div className="mt-7 grid grid-cols-2 gap-3">
                    {!startedAt ? (
                        <button
                            type="button"
                            onClick={startNow}
                            data-testid={CUES.pacerStartBtn}
                            className="col-span-2 inline-flex items-center justify-center gap-2 h-12 rounded-2xl bg-primary text-primary-foreground font-bold tracking-tight shadow-neon hover:-translate-y-[2px] active:scale-[0.98] transition-all"
                        >
                            <Play size={16} weight="fill" />
                            Start pacing
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={() => {
                                    setStartedAt(null);
                                    completedRef.current = false;
                                    lastBoundaryRef.current = {
                                        rep: -1,
                                        phaseIdx: -1,
                                        second: -1,
                                    };
                                }}
                                className="inline-flex items-center justify-center gap-2 h-12 rounded-2xl border border-foreground/15 bg-foreground/5 hover:bg-foreground/10 active:scale-[0.98] transition-all font-semibold"
                            >
                                <ArrowsClockwise size={14} weight="bold" />
                                Reset
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    completedRef.current = true;
                                    onComplete?.(repNumber);
                                }}
                                data-testid={CUES.pacerStopBtn}
                                className="inline-flex items-center justify-center gap-2 h-12 rounded-2xl bg-foreground text-background font-bold tracking-tight active:scale-[0.98] transition-all"
                            >
                                <Stop size={14} weight="fill" />
                                Stop
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
